import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Send, Sparkles } from 'lucide-react';
import { analystApi, type AnalystSummary, type SearchOption } from '../../services/api';
import { isoDaysAgo } from './scope';
import { SignalBadge } from './RefText';

/** Widget della dashboard (agentanalyst.md §13.2): domanda rapida e analisi recenti. */

const STATUS_LABEL: Record<string, string> = {
  COMPLETED: 'completata', FAILED: 'interrotta', CANCELLED: 'annullata', NEEDS_INPUT: 'attende risposta',
};

export function AnalystWidget({ summary, onStarted }: { summary: AnalystSummary | null; onStarted: () => void }) {
  const navigate = useNavigate();
  const [searches, setSearches] = useState<SearchOption[]>([]);
  const [searchId, setSearchId] = useState('');
  const [days, setDays] = useState(7);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    analystApi.searches()
      .then((list) => setSearches(list.filter((s) => s.selectable)))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  const ask = async () => {
    if (!searchId || !prompt.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await analystApi.startRun({ prompt: prompt.trim(), scope: { primary: { searchId, from: isoDaysAgo(days), to: null } } });
      onStarted();
      navigate(`/analyst?c=${r.conversationId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-blue-400" />
        <h3 className="font-semibold text-sm">Chiedi all'analista</h3>
      </div>
      <div className="grid md:grid-cols-3 gap-2">
        <select value={searchId} onChange={(e) => setSearchId(e.target.value)} className="md:col-span-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white">
          <option value="">Search TRAC…</option>
          {searches.map((s) => <option key={s.id} value={s.id}>{s.name ?? 'senza nome'} (ID: {s.id})</option>)}
        </select>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white">
          <option value={1}>Ultime 24 ore</option>
          <option value={7}>Ultimi 7 giorni</option>
          <option value={30}>Ultimi 30 giorni</option>
        </select>
      </div>
      <div className="flex gap-2">
        <input value={prompt} onChange={(e) => setPrompt(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void ask(); }}
          placeholder="Es. Com'è andata la conversazione questa settimana?"
          className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500" />
        <button type="button" onClick={ask} disabled={busy || !searchId || !prompt.trim()}
          className="bg-blue-700 hover:bg-blue-600 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg px-3 py-2 text-sm flex items-center gap-1">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
      {error && <p className="text-xs text-red-300">{error}</p>}

      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Analisi recenti</p>
        {summary && summary.latest.length > 0 ? (
          <ul className="space-y-1">
            {summary.latest.map((r) => (
              <li key={r.id}>
                <button type="button" onClick={() => navigate(`/analyst?c=${r.conversation_id ?? ''}`)} className="w-full flex items-center gap-2 text-left text-xs text-gray-300 hover:bg-gray-800 rounded px-2 py-1">
                  <span className="flex-1 truncate">{r.prompt}</span>
                  {r.status === 'COMPLETED' ? <SignalBadge signal={r.signal} /> : <span className="text-gray-500">{STATUS_LABEL[r.status] ?? 'in corso'}</span>}
                </button>
              </li>
            ))}
          </ul>
        ) : <p className="text-xs text-gray-500">Nessuna analisi ancora.</p>}
      </div>
    </div>
  );
}
